import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    FiAlertCircle,
    FiChevronDown,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiSend,
    FiX,
} from 'react-icons/fi';
import {
    ArrowDownAZ,
    ArrowDownNarrowWide,
    ArrowUpAZ,
    ArrowUpNarrowWide,
    Columns3,
    Rows3,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../common/BackButton';
import Toast from '../common/Toast';
import AssignmentContextMenu from '../schedule/components/AssignmentContextMenu';
import EditAssignment from '../assignments/EditAssignment';
import {
    getQuotationJobById,
    getQuotationRowsPage,
    getSentQuotationDepartmentsForJobs,
    subscribeToQuotations,
    unsubscribeFromQuotations,
} from '../../lib/supabase/quotations';
import { isFinalPricingRequired, computeEstimationTotals, computeEffectivePricing, resolvePricingColumn } from '../../lib/supabase/finalPricingModes';
import PricingColumnCell, { DEPT_META, getColumnLabel } from '../pricing/PricingColumnCell';
import PricingColumnsPopover, { sanitizeColumns } from '../pricing/PricingColumnsPopover';
import QuotationEmailSender from './QuotationEmailSender';

const VIEW_PERMISSION = 'view_quotation_page';
const SEND_PERMISSION = 'send_quotation_page';

const PAGE_LIMIT = 50;
const COMPACT_BREAKPOINT = 1024;
const DESKTOP_ROW_HEIGHT = 40;
const COMPACT_CARD_HEIGHT = 138;
const SCROLL_LOAD_THRESHOLD = 400;

const DEFAULT_QUOTATION_COLUMNS = [
    { source: 'all', grouping: 'per-department' },
    { source: 'all', grouping: 'total' },
];

const FIXED_SEPARATE_WIDTHS = { index: 36, jobId: 140, customer: 170, address: 260, department: 110, sent: 90, action: 120 };
const FIXED_COMBINED_WIDTHS = { index: 36, jobId: 140, customer: 170, address: 260, action: 150 };
const PRICING_COLUMN_WIDTH = 160;

const getSeparateColumnWidths = (columnCount) => [
    FIXED_SEPARATE_WIDTHS.index,
    FIXED_SEPARATE_WIDTHS.jobId,
    FIXED_SEPARATE_WIDTHS.customer,
    FIXED_SEPARATE_WIDTHS.address,
    FIXED_SEPARATE_WIDTHS.department,
    ...Array(columnCount).fill(PRICING_COLUMN_WIDTH),
    FIXED_SEPARATE_WIDTHS.sent,
    FIXED_SEPARATE_WIDTHS.action,
];

const getCombinedColumnWidths = (columnCount) => [
    FIXED_COMBINED_WIDTHS.index,
    FIXED_COMBINED_WIDTHS.jobId,
    FIXED_COMBINED_WIDTHS.customer,
    FIXED_COMBINED_WIDTHS.address,
    ...Array(columnCount).fill(PRICING_COLUMN_WIDTH),
    FIXED_COMBINED_WIDTHS.action,
];

const hasExactPermission = (userProfile, permission) =>
    Array.isArray(userProfile?.permissions) &&
    userProfile.permissions.includes(permission);

const loadPref = (key, fallback) => {
    try {
        const value = localStorage.getItem(key);
        return value !== null ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

const savePref = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Ignore localStorage errors.
    }
};

const addressToText = (address) => {
    if (!address) return '—';
    if (typeof address === 'string') return address;

    return [
        address.street,
        address.street_address,
        address.address_line_1,
        address.suburb,
        address.city,
        address.state,
        address.postcode,
    ].filter(Boolean).join(', ') || '—';
};

// Delegates entirely to resolvePricingColumn's canonical `{ source: 'all',
// grouping: 'total' }` formula — the exact same one FinalPricings.js's
// getJobPricingSummary uses for its Total, so the same job can never show
// a different number on the two pages. `estimationTotals` isn't needed
// for the `all` source (it only reads effectivePricing), so it's passed
// as null rather than recomputed here.
const getPricedTotal = (job, requireFinalPricing) => {
    if (!job.effective_pricing) return null;

    return resolvePricingColumn({
        source: 'all',
        grouping: 'total',
        estimationTotals: null,
        effectivePricing: job.effective_pricing,
        requireFinalPricing,
    }).total;
};

const getRowAmount = (row, requireFinalPricing) =>
    row.department
        ? Number(row.pricing?.final_price)
        : getPricedTotal(row.job, requireFinalPricing);

const SentBadge = ({ sent }) => (
    sent ? (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold uppercase tracking-wide">
            Sent
        </span>
    ) : (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 text-[9px] font-semibold uppercase tracking-wide">
            Not sent
        </span>
    )
);

const SeparateRow = React.memo(({ row, index, canSend, sent, columns, requireFinalPricing, onOpen, onOpenAssignment, onEditAssignment }) => {
    const meta = DEPT_META[row.department];
    const Icon = meta.icon;

    const estimationTotals = useMemo(
        () => computeEstimationTotals(row.job.pricing_entries),
        [row.job.pricing_entries]
    );

    return (
        <AssignmentContextMenu
            assignment={row.job}
            onOpenAssignment={onOpenAssignment}
            onEditAssignment={onEditAssignment}
            minimal
        >
            <tr
                className="border-b border-gray-100 transition-colors hover:bg-gray-50"
                style={{ height: DESKTOP_ROW_HEIGHT }}
            >
                <td className="px-1 py-1 text-center text-[10px] text-gray-300 font-mono">{index + 1}</td>
                <td className="px-2 py-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-semibold text-gray-900 truncate">{row.job.unique_id || '—'}</span>
                        {row.job.is_cancelled && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                        )}
                    </div>
                </td>
                <td className="px-2 py-1">
                    <span className="text-xs text-gray-700 truncate block" title={row.job.customer_name || ''}>
                        {row.job.customer_name || '—'}
                    </span>
                </td>
                <td className="px-2 py-1">
                    <span className="text-xs text-gray-500 truncate block" title={addressToText(row.job.address)}>
                        {addressToText(row.job.address)}
                    </span>
                </td>
                <td className="px-2 py-1">
                    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.on}`}>
                        <Icon className={`w-3 h-3 shrink-0 ${meta.iconOn}`} />
                        {row.departmentLabel}
                    </span>
                </td>
                {columns.map(column => (
                    <td key={`${column.source}-${column.grouping}`} className={`px-2 py-1 ${column.grouping === 'total' ? 'text-right' : ''}`}>
                        <PricingColumnCell
                            column={column}
                            estimationTotals={estimationTotals}
                            effectivePricing={row.job.effective_pricing}
                            requireFinalPricing={requireFinalPricing}
                            department={row.department}
                        />
                    </td>
                ))}
                <td className="px-2 py-1 text-center">
                    <SentBadge sent={sent} />
                </td>
                <td className="px-2 py-1">
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => onOpen(row)}
                            disabled={!canSend}
                            className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border transition-colors bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FiSend className="w-3 h-3" />
                            Send
                        </button>
                    </div>
                </td>
            </tr>
        </AssignmentContextMenu>
    );
});

const SeparateCard = React.memo(({ row, canSend, sent, columns, requireFinalPricing, onOpen }) => {
    const meta = DEPT_META[row.department];
    const Icon = meta.icon;

    const estimationTotals = useMemo(
        () => computeEstimationTotals(row.job.pricing_entries),
        [row.job.pricing_entries]
    );

    return (
        <div
            className="final-pricing-card quotation-card mx-3 my-1.5 rounded-lg border border-gray-200 bg-white p-3 flex flex-col gap-2"
            style={{ minHeight: COMPACT_CARD_HEIGHT - 12 }}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-bold text-gray-900">{row.job.unique_id || '—'}</span>
                        {row.job.is_cancelled && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-600 truncate mt-0.5">{row.job.customer_name || '—'}</p>
                    <p className="text-[10px] text-gray-400 truncate">{addressToText(row.job.address)}</p>
                </div>
                <SentBadge sent={sent} />
            </div>

            <span className={`inline-flex items-center gap-1 self-start rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.on}`}>
                <Icon className={`w-3 h-3 shrink-0 ${meta.iconOn}`} />
                {row.departmentLabel}
            </span>

            <div className="flex flex-col gap-1.5 pt-1.5 border-t border-gray-100">
                {columns.map(column => (
                    <div key={`${column.source}-${column.grouping}`} className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-gray-400 font-medium shrink-0">{getColumnLabel(column)}</span>
                        <PricingColumnCell
                            column={column}
                            estimationTotals={estimationTotals}
                            effectivePricing={row.job.effective_pricing}
                            requireFinalPricing={requireFinalPricing}
                            department={row.department}
                        />
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => onOpen(row)}
                disabled={!canSend}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border transition-colors bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <FiSend className="w-3 h-3" />
                Send quotation
            </button>
        </div>
    );
});

const CombinedRow = React.memo(({ row, index, canSend, requireFinalPricing, columns, onOpen, onOpenAssignment, onEditAssignment }) => {
    const estimationTotals = useMemo(
        () => computeEstimationTotals(row.job.pricing_entries),
        [row.job.pricing_entries]
    );

    return (
        <AssignmentContextMenu
            assignment={row.job}
            onOpenAssignment={onOpenAssignment}
            onEditAssignment={onEditAssignment}
            minimal
        >
            <tr
                className="border-b border-gray-100 transition-colors hover:bg-gray-50"
                style={{ height: DESKTOP_ROW_HEIGHT }}
            >
                <td className="px-1 py-1 text-center text-[10px] text-gray-300 font-mono">{index + 1}</td>
                <td className="px-2 py-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-semibold text-gray-900 truncate">{row.job.unique_id || '—'}</span>
                        {row.job.is_cancelled && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                        )}
                    </div>
                </td>
                <td className="px-2 py-1">
                    <span className="text-xs text-gray-700 truncate block" title={row.job.customer_name || ''}>
                        {row.job.customer_name || '—'}
                    </span>
                </td>
                <td className="px-2 py-1">
                    <span className="text-xs text-gray-500 truncate block" title={addressToText(row.job.address)}>
                        {addressToText(row.job.address)}
                    </span>
                </td>
                {columns.map(column => (
                    <td key={`${column.source}-${column.grouping}`} className={`px-2 py-1 ${column.grouping === 'total' ? 'text-right' : ''}`}>
                        <PricingColumnCell
                            column={column}
                            estimationTotals={estimationTotals}
                            effectivePricing={row.job.effective_pricing}
                            requireFinalPricing={requireFinalPricing}
                        />
                    </td>
                ))}
                <td className="px-2 py-1">
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => onOpen(row)}
                            disabled={!canSend}
                            className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border transition-colors bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FiSend className="w-3 h-3" />
                            Send
                        </button>
                    </div>
                </td>
            </tr>
        </AssignmentContextMenu>
    );
});

const CombinedCard = React.memo(({ row, canSend, requireFinalPricing, columns, onOpen }) => {
    const estimationTotals = useMemo(
        () => computeEstimationTotals(row.job.pricing_entries),
        [row.job.pricing_entries]
    );

    return (
        <div
            className="final-pricing-card quotation-card mx-3 my-1.5 rounded-lg border border-gray-200 bg-white p-3 flex flex-col gap-2"
            style={{ minHeight: COMPACT_CARD_HEIGHT - 12 }}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-bold text-gray-900">{row.job.unique_id || '—'}</span>
                        {row.job.is_cancelled && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-600 truncate mt-0.5">{row.job.customer_name || '—'}</p>
                    <p className="text-[10px] text-gray-400 truncate">{addressToText(row.job.address)}</p>
                </div>
            </div>

            <div className="flex flex-col gap-1.5 pt-1.5 border-t border-gray-100">
                {columns.map(column => (
                    <div key={`${column.source}-${column.grouping}`} className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-gray-400 font-medium shrink-0">{getColumnLabel(column)}</span>
                        <PricingColumnCell
                            column={column}
                            estimationTotals={estimationTotals}
                            effectivePricing={row.job.effective_pricing}
                            requireFinalPricing={requireFinalPricing}
                        />
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => onOpen(row)}
                disabled={!canSend}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border transition-colors bg-blue-600 text-white border-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <FiSend className="w-3 h-3" />
                Send quotation
            </button>
        </div>
    );
});

const FilterBar = ({
    search,
    setSearch,
    jobType,
    setJobType,
    cancelledStatus,
    setCancelledStatus,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    showPartial,
    setShowPartial,
    columns,
    setColumns,
    loadedCount,
    hasMore,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };

        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const activeCount = [
        jobType !== 'all',
        cancelledStatus !== 'active',
    ].filter(Boolean).length;

    const sorts = [
        { value: 'newest', label: 'Newest first', icon: ArrowDownNarrowWide },
        { value: 'oldest', label: 'Oldest first', icon: ArrowUpNarrowWide },
        { value: 'alphabetical', label: 'Job ID A → Z', icon: ArrowDownAZ },
        { value: 'reverse_alphabetical', label: 'Job ID Z → A', icon: ArrowUpAZ },
        { value: 'highest_price', label: 'Highest price', icon: ArrowDownNarrowWide },
        { value: 'lowest_price', label: 'Lowest price', icon: ArrowUpNarrowWide },
    ];

    const countLabel = `${loadedCount.toLocaleString()}${hasMore ? '+' : ''} row${loadedCount !== 1 ? 's' : ''}`;

    return (
        <div className="final-pricing-toolbar quotation-toolbar flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
            <div className="relative min-w-[160px] max-w-sm flex-1">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search job ID, customer or address…"
                    className="w-full h-8 pl-8 pr-8 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <div className="flex items-center h-8 rounded-md border border-gray-200 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setViewMode('separate')}
                    className={`flex items-center gap-1.5 h-full px-2.5 text-xs font-medium transition-colors ${viewMode === 'separate' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                    <Rows3 className="w-3 h-3" />
                    Separate
                </button>
                <button
                    type="button"
                    onClick={() => setViewMode('combined')}
                    className={`flex items-center gap-1.5 h-full px-2.5 text-xs font-medium transition-colors border-l border-gray-200 ${viewMode === 'combined' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                    <Columns3 className="w-3 h-3" />
                    Combined
                </button>
            </div>

            <PricingColumnsPopover
                columns={columns}
                onChange={setColumns}
                defaultColumns={DEFAULT_QUOTATION_COLUMNS}
                allowPerDepartment={viewMode === 'combined'}
            />

            {viewMode === 'combined' && (
                <button
                    type="button"
                    onClick={() => setShowPartial(value => !value)}
                    className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-all ${showPartial
                        ? 'bg-blue-50 border-blue-400 text-blue-700'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
                        }`}
                >
                    {showPartial ? 'Showing partially priced' : 'Fully priced only'}
                </button>
            )}

            <div className="relative" ref={ref}>
                <button
                    type="button"
                    onClick={() => setOpen(value => !value)}
                    className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium border rounded-md transition-all ${activeCount > 0 || open
                        ? 'bg-blue-50 border-blue-400 text-blue-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-blue-400'
                        }`}
                >
                    <FiFilter className="w-3 h-3" />
                    Filters
                    {activeCount > 0 && (
                        <span className="bg-blue-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                            {activeCount}
                        </span>
                    )}
                    <FiChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-4">
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort</p>
                            <div className="space-y-1">
                                {sorts.map(({ value, label, icon: Icon }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setSortBy(value)}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${sortBy === value
                                            ? 'bg-blue-50 text-blue-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        <Icon className="w-3 h-3" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job type</p>
                            <select
                                value={jobType}
                                onChange={(event) => setJobType(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                <option value="all">All job types</option>
                                <option value="Design">Design</option>
                                <option value="Estimation">Estimation</option>
                                <option value="Design & Estimation">Design & Estimation</option>
                                <option value="Timber">Timber</option>
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cancelled jobs</p>
                            <select
                                value={cancelledStatus}
                                onChange={(event) => setCancelledStatus(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                <option value="active">Active only</option>
                                <option value="all">Active and cancelled</option>
                                <option value="cancelled">Cancelled only</option>
                            </select>
                        </div>

                        {activeCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setJobType('all');
                                    setCancelledStatus('active');
                                    setOpen(false);
                                }}
                                className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200 font-medium"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                )}
            </div>

            <span className="ml-auto text-xs text-gray-400">
                {countLabel}
                {hasMore && <span className="text-gray-300"> · scroll for more</span>}
            </span>
        </div>
    );
};

const PermissionDenied = () => (
    <div className="min-h-[55vh] flex items-center justify-center">
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-red-800">No permission</h1>
            <p className="text-sm text-red-700 mt-2">
                You do not have permission to view the Quotation page.
            </p>
        </div>
    </div>
);

const useIsCompact = () => {
    const [isCompact, setIsCompact] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < COMPACT_BREAKPOINT
    );

    useEffect(() => {
        let frame = null;
        const handler = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
            });
        };

        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('resize', handler);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return isCompact;
};

const Quotation = () => {
    const { company, userProfile } = useAuth();
    const requireFinalPricing = isFinalPricingRequired(company);
    const { companySlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const canView = hasExactPermission(userProfile, VIEW_PERMISSION);
    const canSend = hasExactPermission(userProfile, SEND_PERMISSION);

    const [rows, setRows] = useState([]);
    const [sentMap, setSentMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const [search, setSearch] = useState(() => loadPref('qt_search', ''));
    const [debouncedSearch, setDebouncedSearch] = useState(() => loadPref('qt_search', ''));
    const [jobType, setJobType] = useState(loadPref('qt_job_type', 'all'));
    const [cancelledStatus, setCancelledStatus] = useState(loadPref('qt_cancelled', 'active'));
    const [sortBy, setSortBy] = useState(loadPref('qt_sort', 'newest'));
    const [viewMode, setViewMode] = useState(loadPref('qt_view_mode', 'separate'));
    const [showPartial, setShowPartial] = useState(loadPref('qt_show_partial', true));
    const [columns, setColumns] = useState(() => sanitizeColumns(loadPref('quotation_pricing_columns', DEFAULT_QUOTATION_COLUMNS), DEFAULT_QUOTATION_COLUMNS));

    // Separate-view rows ({ job, department, pricing }) and combined-view
    // rows ({ job }) have different shapes. Clearing `rows` in the same
    // event handler as the mode switch (React batches both updates into one
    // render) prevents a render pass where the new viewMode reads rows still
    // shaped for the old mode — e.g. SeparateRow indexing DEPT_META with an
    // undefined `row.department` from a stale combined-view row.
    const changeViewMode = useCallback((mode) => {
        setRows([]);
        setViewMode(mode);
    }, []);

    const tableContainerRef = useRef(null);
    const offsetRef = useRef(0);
    const isCompact = useIsCompact();

    const [selectedJob, setSelectedJob] = useState(null);
    const [editAssignmentJobId, setEditAssignmentJobId] = useState(null);

    // Handles the "Go to Quotation" email deep link
    // (`/quotations?jobId=…&department=…`, see notifyReadyForQuotation in
    // pricingNotifications.js): fetches that specific job directly (it may
    // not be on whatever page/filter the list is currently showing), filters
    // the list down to it via the normal search box, and opens the send
    // modal for it — same as clicking "Send" on its row — with the
    // department from the link preselected. The deep-link ref guards this so
    // it only fires once per page load, not on every searchParams change
    // (e.g. from the cleanup below, or from later user-driven search edits).
    const deepLinkHandledRef = useRef(false);
    useEffect(() => {
        if (deepLinkHandledRef.current) return;

        const jobId = searchParams.get('jobId');
        if (!jobId || !company?.id) return;

        deepLinkHandledRef.current = true;
        const department = searchParams.get('department');

        (async () => {
            try {
                const job = await getQuotationJobById(company.id, jobId);
                if (!job) {
                    Toast.error('Job from link not found', { position: 'bottom-right' });
                    return;
                }

                setSearch(job.unique_id || '');
                if (canSend) {
                    setSelectedJob({ job, preselectDepartment: department || null });
                } else {
                    Toast.error('You do not have permission to send quotations', { position: 'bottom-right' });
                }
            } catch (error) {
                console.error('Failed to load job from quotation link:', error);
                Toast.error('Failed to load job from link', { position: 'bottom-right' });
            } finally {
                setSearchParams(previous => {
                    const next = new URLSearchParams(previous);
                    next.delete('jobId');
                    next.delete('department');
                    return next;
                }, { replace: true });
            }
        })();
    }, [searchParams, company?.id, canSend, setSearchParams]);

    useEffect(() => savePref('qt_search', search), [search]);
    useEffect(() => savePref('qt_job_type', jobType), [jobType]);
    useEffect(() => savePref('qt_cancelled', cancelledStatus), [cancelledStatus]);
    useEffect(() => savePref('qt_sort', sortBy), [sortBy]);
    useEffect(() => savePref('qt_view_mode', viewMode), [viewMode]);
    useEffect(() => savePref('qt_show_partial', showPartial), [showPartial]);
    useEffect(() => savePref('quotation_pricing_columns', columns), [columns]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const buildFilters = useCallback(() => ({
        search: debouncedSearch,
        jobType,
        cancelledStatus,
        sortBy,
        viewMode,
        showPartial,
        requireFinalPricing,
    }), [debouncedSearch, jobType, cancelledStatus, sortBy, viewMode, showPartial, requireFinalPricing]);

    const mergeSentMap = useCallback(async (jobIds) => {
        if (!company?.id || jobIds.length === 0) return;

        try {
            const map = await getSentQuotationDepartmentsForJobs(company.id, jobIds);
            setSentMap(previous => ({ ...previous, ...map }));
        } catch (error) {
            console.error('Failed to load sent quotation status:', error);
        }
    }, [company?.id]);

    const fetchFirstPage = useCallback(async () => {
        if (!canView || !company?.id) return;

        setLoading(true);
        try {
            const result = await getQuotationRowsPage(company.id, {
                offset: 0,
                limit: PAGE_LIMIT,
                ...buildFilters(),
            });

            setRows(result.data);
            offsetRef.current = result.nextOffset;
            setHasMore(result.hasMore);
            mergeSentMap([...new Set(result.data.map(row => row.job.id))]);
        } catch (error) {
            console.error('Failed to load quotation rows:', error);
            Toast.error('Failed to load quotation rows', { position: 'bottom-right' });
        } finally {
            setLoading(false);
        }
    }, [canView, company?.id, buildFilters, mergeSentMap]);

    const loadMore = useCallback(async () => {
        if (!canView || !company?.id || loadingMore || !hasMore) return;

        setLoadingMore(true);
        try {
            const result = await getQuotationRowsPage(company.id, {
                offset: offsetRef.current,
                limit: PAGE_LIMIT,
                ...buildFilters(),
            });

            setRows(previous => [...previous, ...result.data]);
            offsetRef.current = result.nextOffset;
            setHasMore(result.hasMore);
            mergeSentMap([...new Set(result.data.map(row => row.job.id))]);
        } catch (error) {
            console.error('Failed to load more quotation rows:', error);
            Toast.error('Failed to load more rows', { position: 'bottom-right' });
        } finally {
            setLoadingMore(false);
        }
    }, [canView, company?.id, loadingMore, hasMore, buildFilters, mergeSentMap]);

    useEffect(() => {
        fetchFirstPage();
    }, [fetchFirstPage]);

    useEffect(() => {
        if (!canView || !company?.id) return undefined;

        const channel = subscribeToQuotations(company.id, (payload) => {
            const jobId = payload?.new?.job_id || payload?.old?.job_id;
            const department = payload?.new?.department || payload?.old?.department;
            if (!jobId || !department) return;

            setRows(previous => previous.map(row => {
                if (row.job.id !== jobId) return row;

                const nextFinalPricings = {
                    ...(row.job.final_pricings || {}),
                    [department]: payload.eventType === 'DELETE' ? null : payload.new,
                };

                const estimationTotals = computeEstimationTotals(row.job.pricing_entries);
                const nextEffectivePricing = computeEffectivePricing({ finalPricings: nextFinalPricings, estimationTotals });

                const nextJob = {
                    ...row.job,
                    final_pricings: nextFinalPricings,
                    effective_pricing: nextEffectivePricing,
                };

                if (row.department && row.department !== department) {
                    return { ...row, job: nextJob };
                }

                return {
                    ...row,
                    job: nextJob,
                    pricing: row.department
                        ? {
                            final_price: nextEffectivePricing.departments[row.department].amount,
                            notes: nextEffectivePricing.departments[row.department].notes,
                            source: nextEffectivePricing.departments[row.department].source,
                        }
                        : row.pricing,
                };
            }));
        });

        return () => unsubscribeFromQuotations(channel);
    }, [canView, company?.id]);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return undefined;

        const handler = () => {
            if (loadingMore || !hasMore) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD) {
                loadMore();
            }
        };

        el.addEventListener('scroll', handler, { passive: true });
        return () => el.removeEventListener('scroll', handler);
    }, [hasMore, loadingMore, loadMore]);

    const displayRows = useMemo(() => {
        if (sortBy !== 'highest_price' && sortBy !== 'lowest_price') return rows;

        return [...rows].sort((a, b) => {
            const aAmount = getRowAmount(a, requireFinalPricing);
            const bAmount = getRowAmount(b, requireFinalPricing);

            if (!Number.isFinite(aAmount)) return 1;
            if (!Number.isFinite(bAmount)) return -1;

            return sortBy === 'highest_price' ? bAmount - aAmount : aAmount - bAmount;
        });
    }, [rows, sortBy, requireFinalPricing]);

    const openRow = useCallback((row) => {
        setSelectedJob({ job: row.job, preselectDepartment: row.department || null });
    }, []);

    const openAssignment = useCallback((jobId) => {
        window.open(`/${companySlug}/assignment/${jobId}`, '_blank');
    }, [companySlug]);

    const editAssignment = useCallback((jobId) => {
        setEditAssignmentJobId(jobId);
    }, []);

    const virtualizer = useVirtualizer({
        count: displayRows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: useCallback(
            () => (isCompact ? COMPACT_CARD_HEIGHT : DESKTOP_ROW_HEIGHT),
            [isCompact]
        ),
        getItemKey: index => displayRows[index].rowId,
        overscan: 8,
    });

    useEffect(() => {
        virtualizer.measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompact, viewMode]);

    const virtualRows = virtualizer.getVirtualItems();
    const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length
        ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
        : 0;

    // In `separate` view every row is already scoped to one department, so
    // a `per-department`-grouping column resolves to an empty list (see
    // resolvePricingColumn's `department` param contract) and would render
    // as a blank column. Filter those out here — matches how the Columns
    // popover already hides `per-department` from being added in this view
    // (`allowPerDepartment={viewMode === 'combined'}`).
    const visibleColumns = useMemo(
        () => (viewMode === 'separate' ? columns.filter(column => column.grouping !== 'per-department') : columns),
        [columns, viewMode]
    );

    const columnWidths = useMemo(
        () => (viewMode === 'separate' ? getSeparateColumnWidths(visibleColumns.length) : getCombinedColumnWidths(visibleColumns.length)),
        [viewMode, visibleColumns.length]
    );
    const tableMinWidth = columnWidths.reduce((sum, w) => sum + w, 0);

    if (!canView) return <PermissionDenied />;

    return (
        <>
            <div className="final-pricings-page quotations-page flex flex-col gap-6 pb-10">
                <div className="flex flex-col gap-3">
                    <BackButton
                        text="Back"
                        variant="ghost"
                        size="sm"
                        iconType="chevron"
                        className="text-blue-600 hover:text-blue-800 self-start"
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                                <FiSend className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Quotation</h1>
                                <p className="text-gray-500 text-sm mt-0.5">
                                    Email final pricing to customers as a quotation.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={fetchFirstPage}
                            disabled={loading}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>

                    {!canSend && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                            You have view-only access. Sending quotation emails is disabled.
                        </div>
                    )}
                </div>

                <div className="final-pricing-panel flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                    <FilterBar
                        search={search}
                        setSearch={setSearch}
                        jobType={jobType}
                        setJobType={setJobType}
                        cancelledStatus={cancelledStatus}
                        setCancelledStatus={setCancelledStatus}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        viewMode={viewMode}
                        setViewMode={changeViewMode}
                        showPartial={showPartial}
                        setShowPartial={setShowPartial}
                        columns={columns}
                        setColumns={setColumns}
                        loadedCount={displayRows.length}
                        hasMore={hasMore}
                    />

                    <div ref={tableContainerRef} className="final-pricing-scroll overflow-auto" style={{ height: '75vh' }}>
                        {loading ? (
                            <div className="h-40 flex items-center justify-center">
                                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <span className="ml-3 text-sm text-gray-500">Loading…</span>
                            </div>
                        ) : displayRows.length === 0 ? (
                            <div className="py-16 text-center">
                                <FiFilter className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                <p className="text-sm font-medium text-gray-500">No priced jobs match the current filters</p>
                            </div>
                        ) : isCompact ? (
                            <>
                                {paddingTop > 0 && <div style={{ height: paddingTop }} />}
                                {virtualRows.map(virtualRow => {
                                    const row = displayRows[virtualRow.index];
                                    return (
                                        <div key={row.rowId} data-index={virtualRow.index} ref={virtualizer.measureElement} style={{ display: 'flow-root' }}>
                                        {viewMode === 'separate' ? (
                                        <SeparateCard
                                            key={row.rowId}
                                            row={row}
                                            canSend={canSend}
                                            sent={!!sentMap[row.job.id]?.has?.(row.department)}
                                            columns={visibleColumns}
                                            requireFinalPricing={requireFinalPricing}
                                            onOpen={openRow}
                                        />
                                    ) : (
                                        <CombinedCard
                                            key={row.rowId}
                                            row={row}
                                            canSend={canSend}
                                            requireFinalPricing={requireFinalPricing}
                                            columns={columns}
                                            onOpen={openRow}
                                        />
                                    )}
                                        </div>
                                    );
                                })}
                                {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
                            </>
                        ) : (
                            <table className="w-full text-xs border-collapse" style={{ minWidth: tableMinWidth, tableLayout: 'fixed' }}>
                                <colgroup>
                                    {columnWidths.map((width, index) => (
                                        <col key={index} style={{ width }} />
                                    ))}
                                </colgroup>
                                <thead className="sticky top-0 z-20 bg-gray-50">
                                    <tr>
                                        <th className="px-1 py-2 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-200">#</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Job ID</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Customer</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Address</th>
                                        {viewMode === 'separate' && (
                                            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Department</th>
                                        )}
                                        {visibleColumns.map(column => (
                                            <th key={`${column.source}-${column.grouping}`} className={`px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 ${column.grouping === 'total' ? 'text-right' : 'text-left'}`}>
                                                {getColumnLabel(column)}
                                            </th>
                                        ))}
                                        {viewMode === 'separate' && (
                                            <th className="px-2 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Sent</th>
                                        )}
                                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paddingTop > 0 && (
                                        <tr><td colSpan={columnWidths.length} style={{ height: paddingTop }} /></tr>
                                    )}

                                    {virtualRows.map(virtualRow => {
                                        const row = displayRows[virtualRow.index];
                                        return viewMode === 'separate' ? (
                                            <SeparateRow
                                                key={row.rowId}
                                                row={row}
                                                index={virtualRow.index}
                                                canSend={canSend}
                                                sent={!!sentMap[row.job.id]?.has?.(row.department)}
                                                columns={visibleColumns}
                                                requireFinalPricing={requireFinalPricing}
                                                onOpen={openRow}
                                                onOpenAssignment={openAssignment}
                                                onEditAssignment={editAssignment}
                                            />
                                        ) : (
                                            <CombinedRow
                                                key={row.rowId}
                                                row={row}
                                                index={virtualRow.index}
                                                canSend={canSend}
                                                requireFinalPricing={requireFinalPricing}
                                                columns={columns}
                                                onOpen={openRow}
                                                onOpenAssignment={openAssignment}
                                                onEditAssignment={editAssignment}
                                            />
                                        );
                                    })}

                                    {paddingBottom > 0 && (
                                        <tr><td colSpan={columnWidths.length} style={{ height: paddingBottom }} /></tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {loadingMore && (
                            <div className="flex items-center justify-center py-4 gap-2">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-gray-500">Loading more…</span>
                            </div>
                        )}

                        {!loading && !loadingMore && !hasMore && displayRows.length > 0 && (
                            <p className="text-center text-[11px] text-gray-400 py-4">
                                All {displayRows.length.toLocaleString()} rows loaded
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {selectedJob && (
                <QuotationEmailSender
                    job={selectedJob.job}
                    preselectDepartment={selectedJob.preselectDepartment}
                    company={company}
                    userProfile={userProfile}
                    onClose={() => setSelectedJob(null)}
                    onSent={({ jobId, departments }) => {
                        setSentMap(previous => {
                            const next = new Set(previous[jobId] || []);
                            departments.forEach(department => next.add(department));
                            return { ...previous, [jobId]: next };
                        });
                    }}
                />
            )}

            {editAssignmentJobId && (
                <EditAssignment
                    modalJobId={editAssignmentJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => setEditAssignmentJobId(null)}
                />
            )}
        </>
    );
};

export default Quotation;
